import { COOP_LEAF_POLICIES } from '@/features/content/components/RejectWithPoliciesDialog';
import type { ModerationIntelligence, SignalScore } from '../types';

export type HistoryRiskIndicator = {
  id: string;
  label: string;
  score: number;
  /** Full tooltip (e.g. "Hate Speech 0.020"). */
  title: string;
  /** Non-score metadata chip (GPU / Cloud / Frontier model). */
  kind?: 'score' | 'meta';
};

/** Shorten Hugging Face / Bedrock model ids for chip labels. */
export function shortModelLabel(modelId: string): string {
  const trimmed = modelId.trim();
  if (!trimmed) return trimmed;
  const slash = trimmed.lastIndexOf('/');
  return slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
}

/**
 * Cloud / Bedrock deep models (e.g. openai.gpt-5.6-luna) must not use the GPU chip.
 * Local vision (Gemma) stays under GPU; tipmi.frontier.v1 stays under Frontier.
 */
export function isCloudDeepModel(modelId: string): boolean {
  const m = modelId.trim().toLowerCase();
  if (!m) return false;
  if (m.includes('gemma')) return false;
  return (
    m.includes('openai')
    || m.includes('luna')
    || m.includes('bedrock')
    || m.includes('anthropic')
    || m.includes('claude')
    || m.includes('nova')
    || m.includes('gpt-')
    || m.startsWith('global.amazon')
    || m.startsWith('us.amazon')
    || m.startsWith('eu.amazon')
  );
}

type CanonicalPolicy = (typeof COOP_LEAF_POLICIES)[number];

/** Compact chip labels for the 12 Coop leaf Policies (full name in tooltip). */
const POLICY_CHIP_LABEL: Record<CanonicalPolicy, string> = {
  'Child Safety': 'Child safety',
  'Violence / Threats': 'Violence',
  'Harassment / Bullying': 'Harassment',
  'Hate Speech': 'Hate speech',
  'Sexual / Explicit Content': 'Sexual/explicit',
  'Privacy & Doxxing': 'Privacy',
  'Self-Harm & Suicide': 'Self-harm',
  Spam: 'Spam',
  'Impersonation / Fake Accounts': 'Impersonation',
  'Illicit Substances': 'Illicit substances',
  'Weapons & Dangerous Goods': 'Weapons',
  'Copyright / Intellectual Property': 'Copyright',
};

/** media-manager `policy_confidence` also writes these slug keys alongside display names. */
const POLICY_SLUG: Record<CanonicalPolicy, string> = {
  'Child Safety': 'child_safety',
  'Violence / Threats': 'violence_threats',
  'Harassment / Bullying': 'harassment',
  'Hate Speech': 'hate_speech',
  'Sexual / Explicit Content': 'sexual_explicit',
  'Privacy & Doxxing': 'privacy_doxxing',
  'Self-Harm & Suicide': 'self_harm',
  Spam: 'spam',
  'Impersonation / Fake Accounts': 'impersonation',
  'Illicit Substances': 'illicit_substances',
  'Weapons & Dangerous Goods': 'weapons',
  'Copyright / Intellectual Property': 'copyright',
};

function readFiniteScore(
  channel: Record<string, number> | undefined,
  key: string,
): number | undefined {
  if (!channel) return undefined;
  const value = channel[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Bar fill colors — same bands as the former SignalScoreBar local helper. */
export function scoreBarColor(score: number): string {
  if (score < 0.15) return 'bg-green-500';
  if (score < 0.25) return 'bg-blue-500';
  if (score < 0.55) return 'bg-yellow-500';
  if (score < 0.85) return 'bg-orange-500';
  return 'bg-red-500';
}

/** Neutral chip while Coop is still scanning — zeros are not a clear pass yet. */
export function pendingScoreChipClass(): string {
  return 'border-border/70 bg-muted/40 text-muted-foreground';
}

/** Neutral bar fill while Coop is still scanning. */
export function pendingScoreBarColor(): string {
  return 'bg-muted-foreground/35';
}

/** Chip / badge surface colors matching the same risk bands. */
export function scoreChipClass(score: number): string {
  if (score < 0.15) {
    return 'border-green-200 bg-green-100 text-green-800 dark:border-lime-400/30 dark:bg-lime-400/10 dark:text-lime-300';
  }
  if (score < 0.25) {
    return 'border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-300';
  }
  if (score < 0.55) {
    return 'border-yellow-200 bg-yellow-100 text-yellow-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300';
  }
  if (score < 0.85) {
    return 'border-orange-200 bg-orange-100 text-orange-800 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-300';
  }
  return 'border-red-200 bg-red-100 text-red-800 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-300';
}

function isCombinedRiskSignal(signal: SignalScore): boolean {
  const id = signal.signal_id.toLowerCase();
  const term = (signal.term ?? '').toLowerCase();
  return id.includes('combined_risk') || term === 'combined_risk';
}

function isPolicyMaxSignal(signal: SignalScore): boolean {
  const id = signal.signal_id.toLowerCase();
  const term = (signal.term ?? '').toLowerCase();
  return id.includes('policy_max_confidence') || term === 'policy_max_confidence';
}

function isSafetySignal(signal: SignalScore): boolean {
  const channel = (signal.channel ?? '').toLowerCase();
  const id = signal.signal_id.toLowerCase();
  return channel === 'safety' || id.startsWith('safety.');
}

function isPolicyConfidenceSignal(signal: SignalScore): boolean {
  const channel = (signal.channel ?? '').toLowerCase();
  const id = signal.signal_id.toLowerCase();
  return channel === 'policy_confidence' || id.startsWith('policy_confidence.');
}

/** Title-case a snake_case term: self_harm → Self harm. */
function formatTermLabel(term: string): string {
  const spaced = term.replace(/_/g, ' ').trim();
  if (!spaced) return term;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Prefer structured signals; fall back to vectors.moderation.combined_risk.
 * Returns null when intelligence has no combined risk value.
 */
export function pickCombinedRisk(
  intelligence: ModerationIntelligence | null | undefined,
): number | null {
  if (!intelligence) return null;

  const fromSignals = (intelligence.signals ?? []).find(isCombinedRiskSignal);
  if (fromSignals != null && Number.isFinite(fromSignals.score)) {
    return fromSignals.score;
  }

  const fromVectors = intelligence.vectors?.moderation?.combined_risk;
  if (typeof fromVectors === 'number' && Number.isFinite(fromVectors)) {
    return fromVectors;
  }

  return null;
}

/**
 * Phase C routing SoT: max of the 12 Policy confidences.
 * Prefer signals, then moderation.policy_max_confidence, then policy_confidence channel.
 */
export function pickPolicyMaxConfidence(
  intelligence: ModerationIntelligence | null | undefined,
): number | null {
  if (!intelligence) return null;

  const fromSignals = (intelligence.signals ?? []).find(isPolicyMaxSignal);
  if (fromSignals != null && Number.isFinite(fromSignals.score)) {
    return fromSignals.score;
  }

  const fromModeration = intelligence.vectors?.moderation?.policy_max_confidence;
  if (typeof fromModeration === 'number' && Number.isFinite(fromModeration)) {
    return fromModeration;
  }

  const fromChannel = intelligence.vectors?.policy_confidence?.policy_max_confidence;
  if (typeof fromChannel === 'number' && Number.isFinite(fromChannel)) {
    return fromChannel;
  }

  return null;
}

function hasPolicyConfidenceChannel(
  intelligence: ModerationIntelligence,
): boolean {
  if ((intelligence.signals ?? []).some(isPolicyConfidenceSignal)) return true;
  const channel = intelligence.vectors?.policy_confidence;
  if (!channel || typeof channel !== 'object') return false;
  return Object.keys(channel).some(
    (key) => key !== 'policy_max_confidence' && Number.isFinite(channel[key]),
  );
}

function pickPolicyConfidenceIndicators(
  intelligence: ModerationIntelligence,
): HistoryRiskIndicator[] {
  const channel = intelligence.vectors?.policy_confidence ?? {};
  const fromSignals = (intelligence.signals ?? []).filter(isPolicyConfidenceSignal);
  const signalByName = new Map<string, number>();
  for (const signal of fromSignals) {
    if (!Number.isFinite(signal.score)) continue;
    const name = (signal.display_name || signal.term || '').trim();
    if (name && name !== 'policy_max_confidence') {
      signalByName.set(name, signal.score);
    }
  }

  const out: HistoryRiskIndicator[] = [];
  for (const policy of COOP_LEAF_POLICIES) {
    const resolved =
      readFiniteScore(channel, policy)
      ?? readFiniteScore(channel, POLICY_SLUG[policy])
      ?? signalByName.get(policy)
      ?? 0;
    const short = POLICY_CHIP_LABEL[policy];
    out.push({
      id: `policy_confidence.${policy}`,
      label: `${short} ${resolved.toFixed(2)}`,
      score: resolved,
      title: `${policy} ${resolved.toFixed(3)}`,
    });
  }

  out.sort((a, b) => b.score - a.score);
  return out;
}

function pickLegacySafetyIndicators(
  intelligence: ModerationIntelligence,
  seen: Set<string>,
): HistoryRiskIndicator[] {
  const safetyFromSignals = (intelligence.signals ?? []).filter(isSafetySignal);
  const safety: HistoryRiskIndicator[] = [];

  for (const signal of safetyFromSignals) {
    if (!Number.isFinite(signal.score)) continue;
    const id = signal.signal_id || `safety.${signal.term}`;
    if (seen.has(id)) continue;
    seen.add(id);
    const short = formatTermLabel(signal.term || id.replace(/^safety\./i, ''));
    safety.push({
      id,
      label: `${short} ${signal.score.toFixed(2)}`,
      score: signal.score,
      title: `${signal.display_name || `Safety: ${short}`} ${signal.score.toFixed(3)}`,
    });
  }

  // Fall back to vectors.safety when signals omit the channel.
  const safetyVectors = intelligence.vectors?.safety;
  if (safetyVectors && safety.length === 0) {
    for (const [term, score] of Object.entries(safetyVectors)) {
      if (typeof score !== 'number' || !Number.isFinite(score)) continue;
      const id = `safety.${term}`;
      if (seen.has(id)) continue;
      seen.add(id);
      const short = formatTermLabel(term);
      safety.push({
        id,
        label: `${short} ${score.toFixed(2)}`,
        score,
        title: `Safety: ${short} ${score.toFixed(3)}`,
      });
    }
  }

  safety.sort((a, b) => b.score - a.score);
  return safety;
}

function pickModelMetaIndicators(
  intelligence: ModerationIntelligence,
): HistoryRiskIndicator[] {
  const rawGpu = (intelligence.gpu_model ?? '').trim();
  const rawFrontier = (intelligence.frontier_model ?? '').trim();
  // Cloud deep (e.g. Luna) may arrive on gpu_model or frontier_model — label Cloud, not GPU/Frontier.
  const gpuIsCloud = Boolean(rawGpu) && isCloudDeepModel(rawGpu);
  const frontierIsCloud = Boolean(rawFrontier) && isCloudDeepModel(rawFrontier);
  const gpuModel = gpuIsCloud ? '' : rawGpu;
  const frontierModel = frontierIsCloud ? '' : rawFrontier;
  const cloudModels = [
    ...new Set(
      [gpuIsCloud ? rawGpu : '', frontierIsCloud ? rawFrontier : ''].filter(Boolean),
    ),
  ];
  const out: HistoryRiskIndicator[] = [];
  if (gpuModel) {
    out.push({
      id: 'meta.gpu_model',
      label: `GPU · ${shortModelLabel(gpuModel)}`,
      score: 0,
      title: `GPU / vision model\n${gpuModel}`,
      kind: 'meta',
    });
  }
  for (const [index, cloudModel] of cloudModels.entries()) {
    out.push({
      id: index === 0 ? 'meta.cloud_model' : `meta.cloud_model.${index}`,
      label: `Cloud · ${shortModelLabel(cloudModel)}`,
      score: 0,
      title: `Cloud deep model\n${cloudModel}`,
      kind: 'meta',
    });
  }
  if (frontierModel) {
    out.push({
      id: 'meta.frontier_model',
      label: `Frontier · ${shortModelLabel(frontierModel)}`,
      score: 0,
      title: `Frontier model\n${frontierModel}`,
      kind: 'meta',
    });
  }
  return out;
}

function withModelTooltip(
  title: string,
  intelligence: ModerationIntelligence,
  scoredByGpu: boolean,
): string {
  const rawGpu = (intelligence.gpu_model ?? '').trim();
  const rawFrontier = (intelligence.frontier_model ?? '').trim();
  const gpuIsCloud = Boolean(rawGpu) && isCloudDeepModel(rawGpu);
  const frontierIsCloud = Boolean(rawFrontier) && isCloudDeepModel(rawFrontier);
  const gpuModel = gpuIsCloud ? '' : rawGpu;
  const frontierModel = frontierIsCloud ? '' : rawFrontier;
  const cloudModels = [
    ...new Set(
      [gpuIsCloud ? rawGpu : '', frontierIsCloud ? rawFrontier : ''].filter(Boolean),
    ),
  ];
  const lines = [
    scoredByGpu && gpuModel ? `Scored by ${gpuModel}` : null,
    !scoredByGpu && gpuModel ? `GPU / vision: ${gpuModel}` : null,
    ...cloudModels.map((cloudModel) => `Cloud: ${cloudModel}`),
    frontierModel ? `Frontier: ${frontierModel}` : null,
  ].filter((line): line is string => Boolean(line));
  return lines.length > 0 ? `${title}\n${lines.join('\n')}` : title;
}

/**
 * Compact history indicators (TM-3756 Phase C):
 * - Lead chip = policy_max_confidence when present (else combined_risk)
 * - Then the 12 Policy confidences when that channel exists
 * - Else legacy safety.* vectors (pre–Phase C cases)
 * - Trailing GPU / Cloud / Frontier model meta chips when known
 */
export function pickHistoryRiskIndicators(
  intelligence: ModerationIntelligence | null | undefined,
): HistoryRiskIndicator[] {
  if (!intelligence) return [];

  const out: HistoryRiskIndicator[] = [];
  const seen = new Set<string>();

  const policyMax = pickPolicyMaxConfidence(intelligence);
  const combined = pickCombinedRisk(intelligence);
  const lead = policyMax ?? combined;
  if (lead != null) {
    const id =
      policyMax != null ? 'moderation.policy_max_confidence' : 'moderation.combined_risk';
    seen.add(id);
    const baseTitle =
      policyMax != null
        ? `Policy max confidence ${lead.toFixed(3)}`
        : `Combined risk ${lead.toFixed(3)}`;
    out.push({
      id,
      label: formatRiskLabel(lead),
      score: lead,
      title: withModelTooltip(baseTitle, intelligence, false),
      kind: 'score',
    });
  }

  if (hasPolicyConfidenceChannel(intelligence)) {
    for (const indicator of pickPolicyConfidenceIndicators(intelligence)) {
      out.push({
        ...indicator,
        title: withModelTooltip(indicator.title, intelligence, true),
        kind: 'score',
      });
    }
    out.push(...pickModelMetaIndicators(intelligence));
    return out;
  }

  for (const indicator of pickLegacySafetyIndicators(intelligence, seen)) {
    out.push({
      ...indicator,
      title: withModelTooltip(indicator.title, intelligence, true),
      kind: 'score',
    });
  }
  out.push(...pickModelMetaIndicators(intelligence));
  return out;
}

const TEXT_KIND_LABEL: Record<string, string> = {
  sexual: 'sexual text',
  hate: 'hate text',
  violence: 'violent text',
  harassment: 'harassment text',
  profanity: 'profanity',
  graphic: 'graphic text',
  insult: 'insulting text',
};

const VISION_KIND_LABEL: Record<string, string> = {
  visual_nudity_gpu: 'nudity',
  visual_nudity: 'nudity',
  visual_violence_gpu: 'violence',
  visual_violence: 'violence',
  visual_csam_gpu: 'child sexual content',
  visual_csam: 'child sexual content',
  visual_hate_score: 'hate imagery',
  visual_hate: 'hate imagery',
  'reason:adult_content': 'adult content',
};

type RiskCause = {
  score: number;
  source: 'text' | 'video';
  /** "the caption" / "the video frame". */
  where: string;
  /** "sexual text" / "nudity". */
  kind: string;
};

function readChannelScore(
  intelligence: ModerationIntelligence,
  channel: string,
  term: string,
): number | null {
  const fromVector = intelligence.vectors?.[channel]?.[term];
  if (typeof fromVector === 'number' && Number.isFinite(fromVector)) return fromVector;
  const fromSignal = (intelligence.signals ?? []).find(
    (signal) => signal.channel === channel && signal.term === term && Number.isFinite(signal.score),
  );
  return fromSignal ? fromSignal.score : null;
}

function textCause(term: string): { where: string; kind: string } | null {
  const split = term.lastIndexOf('_');
  if (split <= 0) return null;
  const kind = TEXT_KIND_LABEL[term.slice(split + 1)];
  if (!kind) return null;
  const prefix = term.slice(0, split);
  const where =
    prefix === 'caption' ? 'the caption' : prefix === 'hashtags' ? 'the hashtags' : `the ${prefix} text`;
  return { where, kind };
}

function collectRiskCauses(intelligence: ModerationIntelligence): RiskCause[] {
  const causes: RiskCause[] = [];
  const text = intelligence.vectors?.text ?? {};
  const seenText = new Set<string>();
  for (const term of Object.keys(text)) {
    const parsed = textCause(term);
    const score = readChannelScore(intelligence, 'text', term);
    if (!parsed || score == null || score <= 0) continue;
    seenText.add(`${parsed.where}|${parsed.kind}`);
    causes.push({ score, source: 'text', ...parsed });
  }
  for (const signal of intelligence.signals ?? []) {
    if (signal.channel !== 'text') continue;
    const parsed = textCause(signal.term);
    if (!parsed || !Number.isFinite(signal.score) || signal.score <= 0) continue;
    const key = `${parsed.where}|${parsed.kind}`;
    if (seenText.has(key)) continue;
    seenText.add(key);
    causes.push({ score: signal.score, source: 'text', ...parsed });
  }

  const moderation = intelligence.vectors?.moderation ?? {};
  for (const [term, kind] of Object.entries(VISION_KIND_LABEL)) {
    const score = readChannelScore(intelligence, 'moderation', term);
    if (score == null || score <= 0) continue;
    if (term in moderation || (intelligence.signals ?? []).some((s) => s.term === term)) {
      causes.push({ score, source: 'video', where: 'the video frame', kind });
    }
  }

  const textmod = readChannelScore(intelligence, 'moderation', 'textmod_risk');
  const textPeak = causes.filter((c) => c.source === 'text').reduce((max, c) => Math.max(max, c.score), 0);
  if (textmod != null && textmod > 0 && textmod > textPeak + 0.02) {
    causes.push({
      score: textmod,
      source: 'text',
      where: 'the caption text',
      kind: 'text risk',
    });
  }
  return causes;
}

function formatCauseScore(score: number): string {
  return score.toFixed(2);
}

/**
 * One sentence for moderators: which signal put the post in the high-risk band.
 * Named policy chips can stay low when caption text floors policy max.
 */
export function explainRiskBandCause(
  intelligence: ModerationIntelligence | null | undefined,
): string | null {
  if (!intelligence) return null;
  const policyMax = pickPolicyMaxConfidence(intelligence);
  if (policyMax == null || policyMax < 0.5) return null;

  const allCauses = collectRiskCauses(intelligence);
  const causes = allCauses.filter((cause) => cause.score >= 0.5 || cause.score >= policyMax - 0.05);
  if (causes.length === 0) return null;

  const textCauses = causes.filter((cause) => cause.source === 'text').sort((a, b) => b.score - a.score);
  const videoPeak = allCauses
    .filter((cause) => cause.source === 'video')
    .sort((a, b) => b.score - a.score)[0];
  const lead = [...causes].sort((a, b) => b.score - a.score)[0];
  if (!lead) return null;

  const leadSentence = `High-risk band came from ${lead.where}, scored ${formatCauseScore(lead.score)} as ${lead.kind}.`;
  if (lead.source === 'text') {
    if (videoPeak && lead.score - videoPeak.score >= 0.15) {
      return `${leadSentence} The video frame ${videoPeak.kind} score was ${formatCauseScore(videoPeak.score)}.`;
    }
    const otherText = textCauses.find((cause) => cause !== lead && lead.score - cause.score < 0.15);
    if (otherText) {
      return `High-risk band came from ${lead.where} (${lead.kind} ${formatCauseScore(lead.score)}) and ${otherText.where} (${otherText.kind} ${formatCauseScore(otherText.score)}).`;
    }
  }
  if (lead.source === 'video') {
    const text = textCauses[0];
    if (text && Math.abs(text.score - lead.score) < 0.15) {
      return `High-risk band came from ${text.where} (${text.kind} ${formatCauseScore(text.score)}) and ${lead.where} (${lead.kind} ${formatCauseScore(lead.score)}).`;
    }
  }
  return leadSentence;
}

/** Reject / escalate rows where moderators need the band cause, including the TipMi ledger line. */
export function historyEntryShowsRiskCause(action: string | undefined, source: string | undefined): boolean {
  const label = (action ?? '').trim();
  if (/tipmi ledger\s*[·•\-–—]\s*rejected/i.test(label)) return true;
  if (!isCoopVerdictAction(label, source)) return false;
  const upper = label.toUpperCase();
  return upper.includes('REJECT') || upper.includes('ESCALAT');
}

/** Coop verdict pills (not every enforcement-action duplicate). */
export function isCoopVerdictAction(action: string | undefined, source: string | undefined): boolean {
  if ((source ?? '').trim().toUpperCase() !== 'COOP') return false;
  const a = (action ?? '').trim();
  // Match "Coop · Rejected" and light separator variants from older clients.
  if (!/^coop\s*[·•\-–—]\s+/i.test(a)) return false;
  const upper = a.toUpperCase();
  return (
    upper.includes('REJECT')
    || upper.includes('APPROVE')
    || upper.includes('ESCALAT')
    || upper.includes('NO ACTION')
    || upper.includes('SCANNING')
    || upper.includes('PENDING')
  );
}

/** True for mid-pipeline “Coop · Scanning” history rows. */
export function isCoopScanningAction(action: string | undefined): boolean {
  return /^coop\s*[·•\-–—]\s*scanning\b/i.test((action ?? '').trim());
}

export function formatRiskLabel(score: number): string {
  return `Risk ${score.toFixed(2)}`;
}
